/**
 * Taking a shopper's picture in without losing it — on any phone.
 *
 * The failure this exists for: a shopper taps the photo button, takes a
 * picture with the camera, and the shop page reloads with the picture gone.
 * Two different things cause it, and both are handled here:
 *
 *  1. The page itself runs out of memory. A camera photo is 12 to 200
 *     megapixels — 48 to 800 MB once decoded — and the widget used to decode
 *     it in full twice before uploading (the quality check and the size
 *     check), then again to show it. Now its size is read from the file's
 *     header, the sharpness check decodes a small copy (or is skipped where
 *     the browser cannot make one safely), and the page draws the server's
 *     preview copy (see `previewImage`) instead of the original.
 *  2. The phone closes the browser while the camera app is open (Android
 *     does this to a background tab when memory is short). The picture never
 *     reaches the page. The answers are saved before the picker opens, the
 *     page notices it was reloaded mid-pick, and the shopper is told plainly
 *     and offered the in-page camera, which never leaves the page.
 *
 * Whatever does reach the page is written to IndexedDB at once, before any
 * processing: if the page dies while uploading, the next load finishes the
 * upload by itself.
 */

// ── Size from the header ─────────────────────────────────────────────────

/** Pixel size of a picture, upright (EXIF/HEIF turns applied). */
export interface PictureSize {
  w: number;
  h: number;
}

/** The first bytes of a blob, on browsers with and without `Blob.arrayBuffer`. */
async function bytesOf(blob: Blob, start: number, end: number): Promise<Uint8Array> {
  const part = blob.slice(start, Math.min(end, blob.size));
  if (typeof part.arrayBuffer === 'function') return new Uint8Array(await part.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(part);
  });
}

const u16be = (b: Uint8Array, i: number): number => ((b[i]! << 8) | b[i + 1]!) >>> 0;
const u32be = (b: Uint8Array, i: number): number => ((b[i]! << 24) | (b[i + 1]! << 16) | (b[i + 2]! << 8) | b[i + 3]!) >>> 0;
const u16le = (b: Uint8Array, i: number): number => (b[i]! | (b[i + 1]! << 8)) >>> 0;
const u24le = (b: Uint8Array, i: number): number => (b[i]! | (b[i + 1]! << 8) | (b[i + 2]! << 16)) >>> 0;
const ascii = (b: Uint8Array, i: number, n: number): string => String.fromCharCode(...b.subarray(i, i + n));

/**
 * The picture's pixel size, read from its header without decoding it: JPEG
 * (with the EXIF turn), PNG, GIF, WebP, BMP and HEIC/HEIF/AVIF (with `irot`).
 * Undefined for anything else, or a header this cannot read.
 */
export async function pictureSize(file: Blob): Promise<PictureSize | undefined> {
  return (await headerSize(file)) ?? (await decodedSize(file));
}

/**
 * A format the header reader does not know (TIFF, JPEG XL, an odd export):
 * decoded to be measured, but only a file small enough that decoding it is
 * safe on any phone. Anything bigger stays unmeasured — advice skipped, the
 * upload never refused.
 */
async function decodedSize(file: Blob): Promise<PictureSize | undefined> {
  if (!(file.size > 0) || file.size > 12 * 1024 * 1024) return undefined;
  try {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file);
      const size = { w: bitmap.width, h: bitmap.height };
      bitmap.close();
      return size.w && size.h ? size : undefined;
    }
  } catch {
    // Falls through to the <img> route.
  }
  if (typeof Image === 'undefined' || typeof URL?.createObjectURL !== 'function') return undefined;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalWidth ? { w: img.naturalWidth, h: img.naturalHeight } : undefined);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(undefined);
    };
    img.src = url;
  });
}

/**
 * The size written in the file's header; see `pictureSize`. Exported for the
 * collage intake, which reads many files at once and must never decode one
 * (a format this reader does not know simply stays unmeasured there).
 */
export async function headerSize(file: Blob): Promise<PictureSize | undefined> {
  try {
    const head = await bytesOf(file, 0, 64 * 1024);
    if (head.length < 16) return undefined;
    // PNG: IHDR is the first chunk.
    if (head[0] === 0x89 && ascii(head, 1, 3) === 'PNG') return { w: u32be(head, 16), h: u32be(head, 20) };
    if (ascii(head, 0, 4) === 'GIF8') return { w: u16le(head, 6), h: u16le(head, 8) };
    if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 4) === 'WEBP') return webpSize(head);
    if (head[0] === 0x42 && head[1] === 0x4d) {
      const h = (head[22]! | (head[23]! << 8) | (head[24]! << 16) | (head[25]! << 24)) >> 0;
      return { w: u16le(head, 18) | (u16le(head, 20) << 16), h: Math.abs(h) };
    }
    if (head[0] === 0xff && head[1] === 0xd8) return await jpegSize(file, head);
    if (ascii(head, 4, 4) === 'ftyp') return await heifSize(file, head);
    return undefined;
  } catch {
    return undefined;
  }
}

function webpSize(b: Uint8Array): PictureSize | undefined {
  const chunk = ascii(b, 12, 4);
  if (chunk === 'VP8X') return { w: u24le(b, 24) + 1, h: u24le(b, 27) + 1 };
  if (chunk === 'VP8L') {
    const bits = (b[21]! | (b[22]! << 8) | (b[23]! << 16) | (b[24]! << 24)) >>> 0;
    return { w: (bits & 0x3fff) + 1, h: ((bits >>> 14) & 0x3fff) + 1 };
  }
  if (chunk === 'VP8 ') return { w: u16le(b, 26) & 0x3fff, h: u16le(b, 28) & 0x3fff };
  return undefined;
}

/** JPEG: walk the segments to the frame header, noting the EXIF orientation on the way. */
async function jpegSize(file: Blob, head: Uint8Array): Promise<PictureSize | undefined> {
  let orientation = 1;
  let at = 2;
  // Segments are read four bytes at a time from wherever they are: a phone's
  // EXIF block with its embedded preview can push the frame header past 64 KB.
  let window = head;
  let windowStart = 0;
  const read = async (offset: number, length: number): Promise<Uint8Array> => {
    if (offset >= windowStart && offset + length <= windowStart + window.length) {
      return window.subarray(offset - windowStart, offset - windowStart + length);
    }
    window = await bytesOf(file, offset, offset + Math.max(length, 64 * 1024));
    windowStart = offset;
    return window.subarray(0, length);
  };
  for (let guard = 0; guard < 400 && at + 4 <= file.size; guard++) {
    const marker = await read(at, 4);
    if (marker[0] !== 0xff) return undefined;
    const type = marker[1]!;
    // Fill bytes and markers without a length.
    if (type === 0xff) { at += 1; continue; }
    if (type === 0xd8 || (type >= 0xd0 && type <= 0xd7) || type === 0x01) { at += 2; continue; }
    const length = u16be(marker, 2);
    if (length < 2) return undefined;
    if (type === 0xe1 && orientation === 1) {
      const seg = await read(at + 4, Math.min(length - 2, 64 * 1024));
      orientation = exifOrientation(seg) ?? 1;
    }
    const frame = (type >= 0xc0 && type <= 0xc3) || (type >= 0xc5 && type <= 0xc7) || (type >= 0xc9 && type <= 0xcb) || (type >= 0xcd && type <= 0xcf);
    if (frame) {
      const sof = await read(at + 4, 5);
      const h = u16be(sof, 1);
      const w = u16be(sof, 3);
      return orientation >= 5 && orientation <= 8 ? { w: h, h: w } : { w, h };
    }
    if (type === 0xda) return undefined; // image data before any frame header
    at += 2 + length;
  }
  return undefined;
}

/** The EXIF orientation (1–8) in an APP1 segment's payload, if there is one. */
export function exifOrientation(seg: Uint8Array): number | undefined {
  if (seg.length < 14 || ascii(seg, 0, 4) !== 'Exif') return undefined;
  const t = 6; // TIFF header start
  const little = seg[t] === 0x49;
  const r16 = (i: number): number => (little ? u16le(seg, i) : u16be(seg, i));
  const r32 = (i: number): number =>
    little ? (seg[i]! | (seg[i + 1]! << 8) | (seg[i + 2]! << 16) | (seg[i + 3]! << 24)) >>> 0 : u32be(seg, i);
  const ifd = t + r32(t + 4);
  if (ifd + 2 > seg.length) return undefined;
  const count = r16(ifd);
  for (let k = 0; k < count; k++) {
    const entry = ifd + 2 + k * 12;
    if (entry + 12 > seg.length) return undefined;
    if (r16(entry) === 0x0112) {
      const value = r16(entry + 8);
      return value >= 1 && value <= 8 ? value : undefined;
    }
  }
  return undefined;
}

/** HEIF/AVIF: the largest `ispe` (the primary picture, not a thumbnail), turned by `irot`. */
async function heifSize(file: Blob, head: Uint8Array): Promise<PictureSize | undefined> {
  // The `meta` box sits near the start of a phone's HEIC; 512 KB covers it.
  const b = head.length >= 512 * 1024 || file.size <= head.length ? head : await bytesOf(file, 0, 512 * 1024);
  let best: PictureSize | undefined;
  let turned = false;
  // `ispe` and `irot` are leaf boxes inside meta/iprp/ipco; scanning for their
  // type codes is simpler than walking the tree and just as exact here.
  for (let i = 4; i + 5 <= b.length; i++) {
    if (i + 16 <= b.length && b[i] === 0x69 && b[i + 1] === 0x73 && b[i + 2] === 0x70 && b[i + 3] === 0x65) {
      // size(4) 'ispe' version/flags(4) width(4) height(4)
      const w = u32be(b, i + 8);
      const h = u32be(b, i + 12);
      if (w > 0 && h > 0 && w < 100_000 && h < 100_000 && (!best || w * h > best.w * best.h)) best = { w, h };
    } else if (b[i] === 0x69 && b[i + 1] === 0x72 && b[i + 2] === 0x6f && b[i + 3] === 0x74) {
      const angle = (b[i + 4]! & 0x03);
      if (angle === 1 || angle === 3) turned = true;
    }
  }
  if (!best) return undefined;
  return turned ? { w: best.h, h: best.w } : best;
}

// ── Memory the phone can spare ───────────────────────────────────────────

/** Pixels a full decode is allowed on this device before we refuse to try. */
export function safeDecodePixels(): number {
  const nav = (typeof navigator !== 'undefined' ? navigator : undefined) as (Navigator & { deviceMemory?: number }) | undefined;
  const memory = nav?.deviceMemory;
  const ua = nav?.userAgent ?? '';
  // iOS reports no deviceMemory and caps a tab's memory hard; old Androids
  // report 1–2 GB. A 12 MP photo decodes to 48 MB.
  if (/iPhone|iPad|iPod/.test(ua) || (memory !== undefined && memory <= 2)) return 12_000_000;
  if (memory !== undefined && memory <= 4) return 24_000_000;
  return 40_000_000;
}

let resizeSupport: Promise<boolean> | undefined;

/** Does `createImageBitmap` honour `resizeWidth`? Probed once on a 4×4 picture. */
export function bitmapResizeSupported(): Promise<boolean> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return Promise.resolve(false);
  return (resizeSupport ??= (async () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 4;
      canvas.height = 4;
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) return false;
      const bitmap = await createImageBitmap(blob, { resizeWidth: 2, resizeHeight: 2, resizeQuality: 'low' });
      const ok = bitmap.width === 2;
      bitmap.close();
      return ok;
    } catch {
      return false;
    }
  })());
}

/**
 * A small decoded copy of a picture (long side ≤ `maxSide`), made without
 * decoding the whole of a huge one where the browser can avoid it. Undefined
 * when that is impossible without risking the page — the caller then simply
 * goes without (a sharpness hint, never a refusal).
 */
export async function smallBitmap(file: Blob, size: PictureSize | undefined, maxSide: number): Promise<ImageBitmap | undefined> {
  if (typeof createImageBitmap !== 'function') return undefined;
  try {
    if (size && (await bitmapResizeSupported())) {
      const k = Math.min(1, maxSide / Math.max(size.w, size.h));
      const options: ImageBitmapOptions = size.w >= size.h
        ? { resizeWidth: Math.max(1, Math.round(size.w * k)), resizeQuality: 'medium' }
        : { resizeHeight: Math.max(1, Math.round(size.h * k)), resizeQuality: 'medium' };
      return await createImageBitmap(file, options);
    }
    // No scaled decode here: only a picture known to be small enough is decoded whole.
    if (!size || size.w * size.h > safeDecodePixels()) return undefined;
    return await createImageBitmap(file);
  } catch {
    return undefined;
  }
}

// ── Fit for the server ───────────────────────────────────────────────────

/** The server's limits (see the upload route): bytes and pixels. */
export const MAX_UPLOAD_BYTES = 40 * 1024 * 1024;
export const MAX_UPLOAD_PIXELS = 100_000_000;
/** A picture made on the phone stays under what every mobile canvas can hold. */
const SHRUNK_PIXELS = 16_000_000;

/**
 * The file as the server will take it. Almost always the file itself,
 * untouched (the original is what prints). Only a picture over the server's
 * limits — a 200-megapixel camera mode, a 60 MB export — is shrunk here to
 * 16 megapixels, more than any product in the shop prints at, rather than
 * refused. Throws only when even that is impossible, with a message saying
 * what to do.
 */
export async function fitForUpload(file: File, size: PictureSize | undefined): Promise<File> {
  const tooHeavy = file.size > MAX_UPLOAD_BYTES;
  const tooBig = size ? size.w * size.h > MAX_UPLOAD_PIXELS : false;
  if (!tooHeavy && !tooBig) return file;
  const fail = (): never => {
    throw new Error('Slika je prevelika za slanje sa ovog uređaja. Izaberi manju sliku ili je sačuvaj kao JPG.');
  };
  if (!/^image\//.test(file.type) && !size) return fail();
  const target = size ?? { w: 4000, h: 4000 };
  const k = Math.min(1, Math.sqrt(SHRUNK_PIXELS / (target.w * target.h)));
  const w = Math.max(1, Math.floor(target.w * k));
  const h = Math.max(1, Math.floor(target.h * k));
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = (await bitmapResizeSupported())
      ? await createImageBitmap(file, { resizeWidth: w, resizeHeight: h, resizeQuality: 'high' })
      : await createImageBitmap(file);
  } catch {
    return fail();
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext('2d');
    if (!context) return fail();
    context.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    canvas.width = canvas.height = 0;
    if (!blob || blob.size > MAX_UPLOAD_BYTES) return fail();
    const name = (file.name || 'slika').replace(/\.[a-z0-9]{1,5}$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    bitmap?.close();
  }
}

// ── Pictures in flight survive a reload ──────────────────────────────────

const DB_NAME = 'personalizy';
const STORE = 'pending-uploads';
/** A picked picture older than this is not resumed: the shopper moved on. */
const PENDING_TTL_MS = 24 * 3600_000;

export interface PendingPicture {
  key: string;
  optionId: string;
  file: File;
  at: number;
  /**
   * The index it was kept under (the key's last part): "0" for a single photo
   * question, "<batch>.<n>" for one photo of a collage's many — so the resumed
   * upload drops exactly its own record, not its neighbours'.
   */
  index?: string;
}

let database: Promise<IDBDatabase | undefined> | undefined;

function openDb(): Promise<IDBDatabase | undefined> {
  return (database ??= new Promise<IDBDatabase | undefined>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(undefined);
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'key' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(undefined);
      request.onblocked = () => resolve(undefined);
      // A private window that never answers must not hold up an upload.
      setTimeout(() => resolve(undefined), 2500);
    } catch {
      resolve(undefined);
    }
  }));
}

/** Runs one request on the store; undefined when storage is unavailable or slow. */
async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise<T | undefined>((resolve) => {
    try {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      const finish = (value: T | undefined) => { clearTimeout(timer); resolve(value); };
      // A request can succeed before its transaction commits. Recovery files
      // are durable only once the transaction completes.
      transaction.oncomplete = () => finish(request.result);
      transaction.onabort = transaction.onerror = () => finish(undefined);
      const timer = setTimeout(() => {
        try { transaction.abort(); } catch { /* already finished */ }
        finish(undefined);
      }, 4000);
    } catch {
      resolve(undefined);
    }
  });
}

/**
 * Where a picture waiting for one question of one product is kept. The index
 * is 0 for a single photo question; a collage keeps each of its many photos
 * under its own string index ("<batch>.<n>").
 */
export const pendingKey = (scope: string, optionId: string, index: number | string = 0): string => `${scope}|${optionId}|${index}`;

/** The index part of a pending key (after its last "|"); undefined for a key without one. */
export function pendingIndexOf(key: string): string | undefined {
  const at = key.lastIndexOf('|');
  return at >= 0 && at < key.length - 1 ? key.slice(at + 1) : undefined;
}

/**
 * Keeps a picked picture until its upload is confirmed. Safari before 14 and
 * some private windows cannot store a File; they store its bytes instead.
 */
export async function keepPending(scope: string, optionId: string, file: File, index: number | string = 0): Promise<void> {
  // Private browsing without IndexedDB must not copy a whole camera file to
  // RAM for a fallback that has nowhere to save it.
  if (!(await openDb())) return;
  const record = { key: pendingKey(scope, optionId, index), optionId, at: Date.now(), name: file.name, type: file.type, file: file as Blob };
  const stored = await withStore('readwrite', (store) => store.put(record));
  if (stored !== undefined) return;
  try {
    const bytes = await bytesOf(file, 0, file.size);
    await withStore('readwrite', (store) => store.put({ ...record, file: new Blob([bytes as BlobPart], { type: file.type }) }));
  } catch {
    /* no storage: the upload simply is not resumable */
  }
}

/** The upload landed (or the shopper removed the picture): forget it. */
export async function dropPending(scope: string, optionId: string, index: number | string = 0): Promise<void> {
  await withStore('readwrite', (store) => store.delete(pendingKey(scope, optionId, index)));
}

/** Pictures picked on this product that never finished uploading. Old ones are cleared. */
export async function pendingFor(scope: string): Promise<PendingPicture[]> {
  const all = await withStore('readonly', (store) => store.getAll());
  const out: PendingPicture[] = [];
  for (const raw of (all ?? []) as Array<{ key: string; optionId: string; at: number; name?: string; type?: string; file: Blob }>) {
    if (!raw?.key?.startsWith(`${scope}|`)) continue;
    if (!raw.file || Date.now() - raw.at > PENDING_TTL_MS) {
      void withStore('readwrite', (store) => store.delete(raw.key));
      continue;
    }
    const file = raw.file instanceof File ? raw.file : new File([raw.file], raw.name || 'slika.jpg', { type: raw.type || raw.file.type });
    out.push({ key: raw.key, optionId: raw.optionId, file, at: raw.at, index: pendingIndexOf(raw.key) });
  }
  return out.sort((a, b) => a.at - b.at);
}

// ── A pick the phone interrupted ─────────────────────────────────────────

const PICKING = 'pz-picking';
/** A picker left open longer than this was abandoned, not interrupted. */
const PICKING_TTL_MS = 20 * 60_000;

/** Notes that the picker (or the camera) is open for this question. */
export function markPicking(scope: string, optionId: string): void {
  const value = JSON.stringify({ scope, optionId, at: Date.now() });
  try {
    sessionStorage.setItem(PICKING, value);
  } catch {
    try { localStorage.setItem(PICKING, value); } catch { /* nowhere to note it */ }
  }
}

/** The picker closed (a picture arrived, or the shopper cancelled). */
export function donePicking(): void {
  for (const store of [safeSession(), safeLocal()]) {
    try { store?.removeItem(PICKING); } catch { /* nothing to clear */ }
  }
}

/**
 * On load: was this page reloaded while a picker was open for one of its
 * questions? Read once and cleared, so the notice shows once.
 */
export function interruptedPick(scope: string): { optionId: string; at: number } | undefined {
  for (const store of [safeSession(), safeLocal()]) {
    try {
      const raw = store?.getItem(PICKING);
      if (!raw) continue;
      store!.removeItem(PICKING);
      const note = JSON.parse(raw) as { scope?: string; optionId?: string; at?: number };
      if (note.scope !== scope || !note.optionId || !note.at || Date.now() - note.at > PICKING_TTL_MS) continue;
      return { optionId: note.optionId, at: note.at };
    } catch {
      /* unreadable note: nothing to say */
    }
  }
  return undefined;
}

function safeSession(): Storage | undefined {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined; } catch { return undefined; }
}
function safeLocal(): Storage | undefined {
  try { return typeof localStorage !== 'undefined' ? localStorage : undefined; } catch { return undefined; }
}

/** Social apps' in-app browsers: the least memory and the most reloads. */
export function inAppBrowser(): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return /FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|Snapchat|Line\/|MicroMessenger|Pinterest/i.test(ua);
}
